import type {
  AbstractionLevel,
  Difficulty,
  EdiblePartOption,
  Lifecycle,
  TaxonomicRank,
  TaxonPayload,
  UsageOption,
  VegetativeType,
} from "@/types";
import { buildImageUrl } from "src/config/images";
import { Vibrant } from "node-vibrant/browser";
import { TAXON_ICONS, USAGE_ICONS } from "../icons";
import { roundToNice } from "../utils";

interface UsageDisplay {
  display: string;
}

export const USAGES: Record<string, UsageDisplay> = {
  herb: {
    display: "Herbe aromatique",
  },
  fruit: {
    display: "Fruit",
  },
  vegetable: {
    display: "Légume",
  },
  flower: {
    display: "Fleur",
  },
  green_manure: {
    display: "Engrais vert",
  },
  cereal: {
    display: "Céréale",
  },
  oilseed: {
    display: "Oléagineux",
  },
  textile: {
    display: "Textile",
  },
  spice: {
    display: "Epice",
  },
  other: {
    display: "Autre",
  },
};

export default class Taxon {
  id: number;
  name: string;
  name_display: string;
  synonyms: string[];
  synonymsDisplay: string[];
  bio: string;
  anecdote: string | null;
  parent: number;
  taxonomicRank: TaxonomicRank;
  lifecycle: Lifecycle;
  cultivated_as: Lifecycle | null;
  usages: UsageOption[];
  edibleParts: EdiblePartOption[];
  seedExpiry: number | null;
  yield_per_m2: number | null;
  annualConsumption: number | null;
  seedDepth: number | null; // mm
  rowSpacing: number | null;
  plantSpacing: number | null;
  plantingDepth: number | null; // cm
  sowingDensity: number | null; // g per linear meter
  abstraction_level: AbstractionLevel;
  has_varieties: boolean;
  product: string | null;
  germinationTime: number | null;
  germinationTemperature: number | null;
  cultivationTime: number | null;
  vegetationZero: number | null;
  maxTemperature: number | null;
  height: number | null;
  vegetativeType: VegetativeType | null;
  imageName: string | null;

  // Growing conditions — display labels come ready-made from the API, so these
  // keep the API's snake_case (no rename) and are consumed as-is.
  lifecycle_display: string | null;
  vegetative_type_display: string | null;
  cultivated_as_display: string | null;
  growth_habit_display: string | null;
  exposition_display: string | null;
  watering_display: string | null;
  soil_display: string | null;
  nutrient_demand_display: string | null;
  pollinator_type_display: string | null;
  sexual_system_display: string | null;
  difficulty: Difficulty | null;
  difficulty_display: string | null;
  germination_needs_light: boolean;
  min_temperature: number | null;
  optimum_temperature: number | null;
  thousand_seed_weight: number | null;
  is_f1: boolean | null;

  // Ecology (French label arrays from the API).
  pests_display: string[];
  pest_deterrent_display: string[];
  attracts_display: string[];
  diseases_display: string[];
  disease_suppressant_display: string[];

  // Natural preparations (PNPP) — French label arrays from the API.
  treatment_preparations_display: string[];
  produced_preparations_display: string[];

  // Encyclopedic free text.
  toxicity: string;
  origin: string;
  nutritionalValues: string;

  // Share of the taxon's fields that are filled in, as a percentage (0-100).
  completeness: number;

  // Pricing & piece weight (read-only, full-season order-of-magnitude; null when no data).
  marketPrice: number | null; // retail €/kg (what a home grower saves)
  producerPrice: number | null; // producer/wholesale €/kg (a farm's revenue)
  unitWeight: number | null; // grams per piece
  soldPerPiece: boolean; // produce sold by the piece → unitWeight is set

  // Dominant colour extracted from the taxon icon, keyed by icon URL. Extraction is
  // expensive (decodes the image), so the result is shared across every instance and
  // every caller (the crop pie chart, the plan's specimen circles, …).
  private static dominantColorCache = new Map<string, string>();

  constructor({
    id,
    name,
    name_display,
    synonyms,
    synonyms_display: synonymsDisplay,
    bio,
    anecdote,
    parent,
    taxonomic_rank: taxonomicRank,
    lifecycle,
    cultivated_as,
    image_name: imageName,
    usages,
    edible_parts: edibleParts,
    seed_expiry: seedExpiry,
    yield_per_m2,
    annual_consumption: annualConsumption,
    seed_depth: seedDepth,
    row_spacing: rowSpacing,
    plant_spacing: plantSpacing,
    planting_depth: plantingDepth,
    sowing_density: sowingDensity,
    abstraction_level,
    has_varieties,
    product,
    germination_time: germinationTime,
    germination_temperature: germinationTemperature,
    cultivation_time: cultivationTime,
    vegetation_zero: vegetationZero,
    max_temperature: maxTemperature,
    height,
    vegetative_type: vegetativeType,
    lifecycle_display,
    vegetative_type_display,
    cultivated_as_display,
    growth_habit_display,
    exposition_display,
    watering_display,
    soil_display,
    nutrient_demand_display,
    pollinator_type_display,
    sexual_system_display,
    difficulty,
    difficulty_display,
    germination_needs_light,
    min_temperature,
    optimum_temperature,
    thousand_seed_weight,
    is_f1,
    pests_display,
    pest_deterrent_display,
    attracts_display,
    diseases_display,
    disease_suppressant_display,
    treatment_preparations_display,
    produced_preparations_display,
    toxicity,
    origin,
    nutritional_values: nutritionalValues,
    completeness,
    market_price: marketPrice,
    producer_price: producerPrice,
    unit_weight: unitWeight,
    sold_per_piece: soldPerPiece,
  }: TaxonPayload) {
    this.id = id;
    this.name = name;
    // F1 varieties surface the hybrid status in name_display (used everywhere):
    // uppercase any existing "f1", or append " F1" when the name doesn't mention it.
    if (is_f1) {
      this.name_display = /f1/i.test(name_display) ? name_display.replace(/f1/gi, "F1") : `${name_display} F1`;
    } else {
      this.name_display = name_display;
    }
    this.synonyms = synonyms;
    this.synonymsDisplay = synonymsDisplay;
    this.bio = bio;
    this.anecdote = anecdote;
    this.parent = parent;
    this.taxonomicRank = taxonomicRank;
    this.lifecycle = lifecycle;
    this.cultivated_as = cultivated_as;
    this.usages = usages;
    this.edibleParts = edibleParts;
    this.seedExpiry = seedExpiry;
    this.yield_per_m2 = yield_per_m2;
    this.annualConsumption = annualConsumption;
    this.seedDepth = seedDepth;
    this.rowSpacing = rowSpacing;
    this.plantSpacing = plantSpacing;
    this.plantingDepth = plantingDepth;
    this.sowingDensity = sowingDensity;
    this.abstraction_level = abstraction_level;
    this.has_varieties = has_varieties;
    this.product = product;
    this.germinationTime = germinationTime;
    this.germinationTemperature = germinationTemperature;
    this.cultivationTime = cultivationTime;
    this.vegetationZero = vegetationZero;
    this.maxTemperature = maxTemperature;
    this.height = height;
    this.vegetativeType = vegetativeType;
    this.imageName = imageName;
    this.lifecycle_display = lifecycle_display;
    this.vegetative_type_display = vegetative_type_display;
    this.cultivated_as_display = cultivated_as_display;
    this.growth_habit_display = growth_habit_display;
    this.exposition_display = exposition_display;
    this.watering_display = watering_display;
    this.soil_display = soil_display;
    this.nutrient_demand_display = nutrient_demand_display;
    this.pollinator_type_display = pollinator_type_display;
    this.sexual_system_display = sexual_system_display;
    this.difficulty = difficulty;
    this.difficulty_display = difficulty_display;
    this.germination_needs_light = germination_needs_light;
    this.min_temperature = min_temperature;
    this.optimum_temperature = optimum_temperature;
    this.thousand_seed_weight = thousand_seed_weight;
    this.is_f1 = is_f1;
    this.pests_display = pests_display;
    this.pest_deterrent_display = pest_deterrent_display;
    this.attracts_display = attracts_display;
    this.diseases_display = diseases_display;
    this.disease_suppressant_display = disease_suppressant_display;
    this.treatment_preparations_display = treatment_preparations_display;
    this.produced_preparations_display = produced_preparations_display;
    this.toxicity = toxicity;
    this.origin = origin;
    this.nutritionalValues = nutritionalValues;
    this.completeness = completeness;
    this.marketPrice = marketPrice;
    this.producerPrice = producerPrice;
    this.unitWeight = unitWeight;
    this.soldPerPiece = soldPerPiece;
  }

  get usagesDisplay(): string[] {
    return this.usages.map((usage) => usage.display);
  }

  get isPerennial(): boolean {
    return this.cultivated_as === "perennial" || (!this.cultivated_as && this.lifecycle === "perennial");
  }

  // Cultivation time as a human-readable string. Beyond 2 years the day count
  // stops being meaningful, so switch to whole/decimal years (e.g. "4562 jours"
  // → "12,5 ans").
  get cultivationTimeDisplay(): string | null {
    if (this.cultivationTime == null) return null;
    if (this.cultivationTime <= 730) return `${this.cultivationTime} jours`;
    const years = this.cultivationTime / 365;
    const rounded = Math.round(years * 10) / 10;
    return `${rounded.toLocaleString("fr-FR")} ans`;
  }

  get taxonIcon(): string {
    return TAXON_ICONS[this.name];
  }

  get usageIcon(): string | undefined {
    return USAGE_ICONS[this.usages[0]?.value];
  }

  // Remove usage icon as default because it is very confusing
  get icon(): string {
    return this.taxonIcon; // || this.usageIcon;
  }

  // The icon's dominant colour as a hex string (null when there's no icon or the
  // extraction fails). Cached per icon URL across instances. Async because decoding
  // the image runs off the main work; callers typically fill a reactive cache from it.
  async getDominantColor(): Promise<string | null> {
    const url = this.icon;
    if (!url) return null;
    const cached = Taxon.dominantColorCache.get(url);
    if (cached) return cached;
    try {
      const palette = await new Vibrant(url).getPalette();
      // Pick the swatch covering the most pixels (dominant by AREA), not simply the most
      // "vibrant" one — otherwise a small, highly-saturated accent (e.g. the eggplant's
      // bright green stem) wins over the colour that actually fills the icon (the purple).
      const swatch = Object.values(palette)
        .filter((s) => s != null)
        .sort((a, b) => b.population - a.population)[0];
      const hex = swatch?.hex ?? null;
      if (hex) Taxon.dominantColorCache.set(url, hex);
      return hex;
    } catch (error) {
      console.warn(`Failed to extract color from ${url}:`, error);
      return null;
    }
  }

  // Piece ⇄ kg conversions for produce sold by the piece (null when no unit weight).
  piecesToKg(pieces: number): number | null {
    return this.unitWeight ? (pieces * this.unitWeight) / 1000 : null;
  }

  kgToPieces(kg: number): number | null {
    return this.unitWeight ? (kg * 1000) / this.unitWeight : null;
  }

  get plantsPerSquareMeter(): number | null {
    if (this.rowSpacing && this.plantSpacing) {
      return (100 / this.rowSpacing) * (100 / this.plantSpacing);
    }
    return null;
  }

  get imageUrl(): string | null {
    return this.imageName ? buildImageUrl(140, this.imageName) : null;
  }

  getYield(area: number): number | null {
    if (!this.yield_per_m2) return null;
    return area * this.yield_per_m2;
  }

  getYieldDisplay(area: number): string | null {
    if (!this.yield_per_m2) return null;
    const yield_ = this.getYield(area);
    if (yield_ === null) return null;
    if (yield_ >= 1) {
      return `${Math.round(yield_ * 100) / 100} kg`;
    } else {
      return `${Math.round(yield_ * 1000)} g`;
    }
  }

  // `householdConsumption` (kg/year for the whole household) overrides the estimate when
  // provided — e.g. the user's own consumption. Otherwise it falls back to this taxon's
  // per-person annual_consumption × family size.
  getSelfSufficiency(area: number, familySize: number, householdConsumption: number | null = null): number | null {
    if (!this.yield_per_m2) return null;
    const consumption = householdConsumption ?? (this.annualConsumption ? this.annualConsumption * familySize : null);
    if (!consumption) return null;
    const yieldValue = area * this.yield_per_m2;
    return Math.round((yieldValue / consumption) * 100);
  }

  /**
   * Exact number of plants on the given area (just rounded to an integer).
   * Returns null when we can't compute it.
   */
  getPlantCount(area: number): number | null {
    if (!this.plantsPerSquareMeter) return null;
    return Math.round(area * this.plantsPerSquareMeter);
  }

  /**
   * Plant count rounded to a "nice" number for small-spaced crops (carrot,
   * radish, …) so we don't pretend to count every seedling. Returns the same
   * value as getPlantCount() for normal crops. The caller decides whether to
   * prefix "~" by comparing both values.
   */
  getPlantCountRounded(area: number): number | null {
    const exact = this.getPlantCount(area);
    if (exact === null) return null;
    // "Small" = either spacing under 15 cm (carrot, radish, spinach, …)
    const minSpacing = Math.min(this.rowSpacing ?? Infinity, this.plantSpacing ?? Infinity);
    if (minSpacing >= 15) return exact;
    return roundToNice(exact);
  }
}
